import { motion } from 'framer-motion'
import { Github, ExternalLink, Star, GitFork } from 'lucide-react'
import { useGithubRepos } from '../../../hooks/useGithubRepos'
import { useTheme } from '../../../contexts/ThemeContext'

const GITHUB_USERNAME = 'amirosseinz'

const containerVariants = {
  offscreen: { opacity: 0 },
  onscreen: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 }
  }
};

const projectVariants = {
  offscreen: { opacity: 0, y: 40, scale: 0.95 },
  onscreen: { 
    opacity: 1, y: 0, scale: 1,
    transition: { type: 'spring', stiffness: 200, damping: 24 }
  }
};

// Map GitHub language to a color
const langColors = {
  Python: '#3572A5',
  JavaScript: '#F1E05A',
  TypeScript: '#3178C6',
  HTML: '#E34C26',
  CSS: '#563D7C',
  Shell: '#89E051',
  Jupyter: '#DA5B0B',
  'Jupyter Notebook': '#DA5B0B',
  Dockerfile: '#384D54',
  Rust: '#DEA584',
  Go: '#00ADD8',
  Java: '#B07219',
  'C++': '#F34B7D',
  C: '#555555',
};

export const ProjectsSection = () => {
  const { repos, loading, error } = useGithubRepos(GITHUB_USERNAME)
  const { isDark } = useTheme();

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-2 border-accent-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm">Loading repositories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-red-400">
        <p>Failed to load repositories</p>
      </div>
    );
  }

  if (!repos.length) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
        <p>No repositories found</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 pb-16">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-10"
      >
        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-neutral-100' : 'text-neutral-900'}`}>
          Projects
        </h2>
        <p className={`text-sm ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
          Open-source repositories — live from{' '}
          <a
            href={`https://github.com/${GITHUB_USERNAME}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-500 hover:underline"
          >
            GitHub
          </a>
        </p>
      </motion.div>

      {/* Projects Grid */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 gap-5"
        variants={containerVariants}
        initial="offscreen"
        whileInView="onscreen"
        viewport={{ once: true, margin: '-50px' }}
      >
        {repos.map((repo) => (
          <motion.div
            key={repo.id}
            variants={projectVariants}
            whileHover={{ 
              y: -6, 
              scale: 1.02,
              transition: { duration: 0.25, ease: 'easeOut' } 
            }}
            className={`
              group p-5 rounded-xl flex flex-col transition-all duration-300 cursor-default border-gradient
              ${isDark 
                ? 'glass-futuristic hover:shadow-xl hover:shadow-accent-500/10' 
                : 'glass-futuristic-light hover:shadow-xl hover:shadow-accent-500/15'
              }
            `}
          >
            {/* Title with accent bar */}
            <div className="flex items-start gap-3 mb-2">
              <div className="w-1 h-5 rounded-full bg-accent-500 flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <h3 className={`font-semibold ${isDark ? 'text-neutral-100' : 'text-neutral-900'}`}>
                {repo.name.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </h3>
            </div>

            {/* Description */}
            <p className={`text-sm leading-relaxed mb-4 flex-grow ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
              {repo.description || 'No description available.'}
            </p>

            {/* Topics / Tech Stack */}
            {repo.topics?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {repo.topics.map((topic) => (
                  <motion.span
                    key={topic}
                    whileHover={{ scale: 1.1, y: -1 }}
                    className={`px-2 py-0.5 rounded text-xs transition-colors ${
                      isDark 
                        ? 'bg-neutral-800 text-neutral-400 hover:bg-accent-500/10 hover:text-accent-400' 
                        : 'bg-neutral-100 text-neutral-600 hover:bg-accent-50 hover:text-accent-600'
                    }`}
                  >
                    {topic}
                  </motion.span>
                ))}
              </div>
            )}

            {/* Meta row: language, stars, forks */}
            <div className={`flex items-center gap-4 mb-3 text-xs ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
              {repo.language && (
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: langColors[repo.language] || '#8B8B8B' }}
                  />
                  {repo.language}
                </span>
              )}
              {repo.stargazers_count > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  {repo.stargazers_count}
                </span>
              )}
              {repo.forks_count > 0 && (
                <span className="inline-flex items-center gap-1">
                  <GitFork className="w-3 h-3" />
                  {repo.forks_count}
                </span>
              )}
            </div>

            {/* Links */}
            <div className="flex gap-3 mt-auto">
              <motion.a
                href={repo.html_url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, x: 2 }}
                whileTap={{ scale: 0.95 }}
                className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${
                  isDark 
                    ? 'text-neutral-400 hover:text-accent-400' 
                    : 'text-neutral-500 hover:text-accent-600'
                }`}
              >
                <Github className="w-3.5 h-3.5" />
                Code
              </motion.a>
              {repo.homepage && (
                <motion.a
                  href={repo.homepage}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, x: 2 }}
                  whileTap={{ scale: 0.95 }}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${
                    isDark 
                      ? 'text-neutral-400 hover:text-accent-400' 
                      : 'text-neutral-500 hover:text-accent-600'
                  }`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Demo
                </motion.a>
              )}
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  )
}